import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import ContactForm from './ContactForm';

function About() {
  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Helmet>
        <title>About Advanced Controls & Automation | Precision CNC Machining in Waterford, NY</title>
        <meta
          name="description"
          content="Learn about Advanced Controls & Automation, a leader in precision CNC machining, tungsten carbide HVAF coatings, and automation systems in Waterford, NY. Serving the Capital District and Upstate New York with American-made quality and rapid lead times."
        />
        <meta
          name="keywords"
          content="precision CNC machining, automation systems, tungsten carbide coatings, Waterford NY, Capital District, Upstate New York, American manufacturing, HVAF coatings, advanced manufacturing"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.acamachine.com/about" />
        <meta property="og:title" content="About Advanced Controls & Automation" />
        <meta property="og:description" content="Discover our expertise in precision CNC machining, tungsten carbide HVAF coatings, and automation systems from our Waterford, NY facility, serving the Capital District and Upstate New York." />
        <meta property="og:image" content="https://www.acamachine.com/preview.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.acamachine.com/about" />
        <meta property="twitter:title" content="About Advanced Controls & Automation" />
        <meta property="twitter:description" content="Discover our expertise in precision CNC machining, tungsten carbide HVAF coatings, and automation systems from our Waterford, NY facility." />
        <meta property="twitter:image" content="https://www.acamachine.com/preview.jpg" />
        <link rel="canonical" href="https://www.acamachine.com/about" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section
        className="pt-36 pb-16"
        style={{
          backgroundColor: '#fff',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='52' viewBox='0 0 60 52'%3E%3Cpath d='M30 0 L60 52 L0 52 Z' fill='none' stroke='rgba(0,0,0,0.05)' stroke-width='1'/%3E%3Cpath d='M0 0 L30 52 L60 0' fill='none' stroke='rgba(0,0,0,0.05)' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 52px',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
          >
            About Advanced Controls & Automation
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Based in <strong>Waterford, NY</strong>, we specialize in <strong>precision CNC machining</strong>, <strong>tungsten carbide HVAF coatings</strong>, and <strong>automation systems</strong>. Proudly serving the <strong>Capital District</strong> and <strong>Upstate New York</strong> with high-quality, <strong>American-made</strong> manufacturing solutions.
          </motion.p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-12">
            Who We Are
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Mission</h3>
              <p className="text-gray-600 text-base">
                We are committed to advancing <strong>American manufacturing</strong> by delivering innovative <strong>precision CNC machining</strong> and <strong>automation systems</strong>. From our <strong>Waterford, NY</strong> facility, we provide rapid, cost-effective solutions to support domestic industries.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Made in America</h3>
              <p className="text-gray-600 text-base">
                Every component we produce is crafted in <strong>Waterford, NY</strong>. We hire local talent, support domestic supply chains, and serve industries like aerospace, energy, and food tech across the <strong>Capital District</strong> and <strong>Upstate New York</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-12">
            What We Do
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-gray-600">
                High-precision CNC milling and turning with HAAS and Bridgeport equipment.
              </p>
            </div>
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-gray-600">
                Advanced <strong>tungsten carbide HVAF coatings</strong> for enhanced durability.
              </p>
            </div>
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-gray-600">
                Custom <strong>automation systems</strong> for optimized manufacturing workflows.
              </p>
            </div>
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-gray-600">
                Real-time quoting with STEP file uploads and material traceability.
              </p>
            </div>
            <div className="flex items-start sm:col-span-2">
              <svg
                className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-gray-600">
                Serving <strong>Upstate New York</strong>, the Northeast, and nationwide with short lead times.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team & Culture */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">
            Our Team & Culture
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            At our <strong>Waterford, NY</strong> facility, our team of engineers, machinists, and automation specialists is dedicated to excellence in <strong>precision CNC machining</strong>, <strong>tungsten carbide coatings</strong>, and <strong>automation systems</strong>. We embrace innovation and craftsmanship to deliver solutions that empower our clients in the <strong>Capital District</strong> and beyond.
          </p>
        </div>
      </section>

      {/* Location */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-6">
            Our Facility in Waterford, NY
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Located at 574 Hudson River Road, <strong>Waterford, NY</strong>, our facility is strategically positioned near I-87 to serve the <strong>Capital District</strong>, <strong>Upstate New York</strong>, and national clients with advanced manufacturing solutions.
          </p>
          <div className="w-full h-96 rounded-lg shadow-lg overflow-hidden">
            <iframe
              title="Advanced Controls & Automation Facility Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2943.6761585628057!2d-73.68329768459414!3d42.77909367916031!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89de1a9f6b7b5b7b%3A0x7c0f0b8d6b7f8b9e!2s574%20Hudson%20River%20Rd%2C%20Waterford%2C%20NY%2012188!5e0!3m2!1sen!2sus!4v1698765432109!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      <Footer />
    </div>
  );
}

export default About;